import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

const TermsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-950 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8 text-amber-500" />
            <h1 className="text-3xl font-bold text-white">Términos y Condiciones</h1>
          </div>
          <p className="text-gray-400">Última actualización: 5 de diciembre de 2024</p>
        </div>

        {/* Content */}
        <div className="bg-gray-900 rounded-2xl border border-gray-800 p-8 space-y-8 text-gray-300">
          
          <section>
            <h2 className="text-xl font-semibold text-white mb-4">1. Información General</h2>
            <p className="mb-4">
              Estos Términos y Condiciones regulan el acceso y uso de la aplicación Reserberry Sport 
              (en adelante, "la Aplicación"), propiedad de:
            </p>
            <p className="mb-2">
              <strong className="text-white">Titular:</strong> Mª Angeles Morales Marquez
            </p>
            <p className="mb-2">
              <strong className="text-white">Marca comercial:</strong> Reserberry
            </p>
            <p className="mb-2">
              <strong className="text-white">Dirección:</strong> Calle Río Retortillo, Mijas, Málaga, España
            </p>
            <p className="mb-2">
              <strong className="text-white">Email:</strong>{' '}
              <a href="mailto:navora.citas@gmail.com" className="text-amber-400 hover:text-amber-300">
                navora.citas@gmail.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">2. Aceptación de los Términos</h2>
            <p>
              Al registrarse y utilizar la Aplicación, el usuario acepta íntegramente estos Términos y Condiciones, 
              así como nuestra{' '}
              <Link to="/privacy" className="text-amber-400 hover:text-amber-300">
                Política de Privacidad
              </Link>
              . Si no está de acuerdo con alguno de estos términos, no debe utilizar la Aplicación.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">3. Descripción del Servicio</h2>
            <p className="mb-4">
              Reserberry Sport es una plataforma que permite a gimnasios y centros deportivos gestionar:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Reservas de clases y actividades.</li>
              <li>Gestión de membresías y bonos.</li>
              <li>Comunicación entre el centro y sus usuarios.</li>
              <li>Procesamiento de pagos online.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">4. Registro de Usuario</h2>
            <p className="mb-4">Para usar la Aplicación, el usuario debe:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Ser mayor de 16 años (o contar con consentimiento parental).</li>
              <li>Proporcionar información veraz y actualizada.</li>
              <li>Mantener la confidencialidad de sus credenciales de acceso.</li>
              <li>Ser invitado por un gimnasio o centro deportivo registrado en la plataforma.</li>
            </ul>
            <p className="mt-4">
              El usuario es responsable de todas las actividades realizadas con su cuenta.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">5. Membresías y Bonos</h2>
            <h3 className="text-lg font-medium text-white mb-2">5.1 Contratación</h3>
            <p className="mb-4">
              Las membresías y bonos se contratan a través de la Aplicación, con los precios y condiciones 
              establecidos por cada gimnasio.
            </p>
            
            <h3 className="text-lg font-medium text-white mb-2">5.2 Renovación Automática</h3>
            <p className="mb-4">
              Las membresías se renuevan automáticamente cada mes hasta que el usuario las cancele. 
              La cancelación debe realizarse antes del inicio del siguiente período de facturación.
            </p>
            
            <h3 className="text-lg font-medium text-white mb-2">5.3 Bonos de Clases</h3>
            <p>
              Los bonos tienen una validez determinada por cada gimnasio. Las clases no utilizadas 
              antes de la fecha de expiración no son reembolsables ni transferibles.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">6. Reservas y Cancelaciones</h2>
            <h3 className="text-lg font-medium text-white mb-2">6.1 Reservas</h3>
            <p className="mb-4">
              Las reservas están sujetas a disponibilidad y a las políticas de cada gimnasio.
            </p>
            
            <h3 className="text-lg font-medium text-white mb-2">6.2 Cancelaciones</h3>
            <p className="mb-4">
              Cada gimnasio establece su política de cancelación. Generalmente, las cancelaciones 
              deben realizarse con un mínimo de antelación para recuperar los créditos.
            </p>
            
            <h3 className="text-lg font-medium text-white mb-2">6.3 No Asistencia</h3>
            <p>
              La no asistencia sin cancelación previa puede resultar en la pérdida de los créditos 
              correspondientes, según la política de cada gimnasio.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">7. Pagos</h2>
            <p className="mb-4">
              Los pagos se procesan de forma segura a través de Stripe. Reserberry no almacena 
              datos de tarjetas de crédito.
            </p>
            <p className="mb-4">
              Los precios mostrados incluyen los impuestos aplicables (IVA).
            </p>
            <p>
              Para cualquier consulta sobre pagos, reembolsos o facturación, contacte con el gimnasio 
              correspondiente o con nuestro soporte en{' '}
              <a href="mailto:navora.citas@gmail.com" className="text-amber-400 hover:text-amber-300">
                navora.citas@gmail.com
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">8. Reembolsos</h2>
            <p className="mb-4">
              La política de reembolsos depende de cada gimnasio. En general:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Las membresías activas pueden cancelarse para evitar futuras renovaciones.</li>
              <li>Los bonos parcialmente utilizados no son reembolsables.</li>
              <li>Casos especiales se evaluarán individualmente contactando con soporte.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">9. Obligaciones del Usuario</h2>
            <p className="mb-4">El usuario se compromete a:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Utilizar la Aplicación de forma lícita y de buena fe.</li>
              <li>No compartir sus credenciales de acceso con terceros.</li>
              <li>No intentar acceder a áreas restringidas de la Aplicación.</li>
              <li>Respetar las normas de cada gimnasio.</li>
              <li>No utilizar la Aplicación para fines fraudulentos.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">10. Propiedad Intelectual</h2>
            <p>
              Todos los contenidos de la Aplicación (textos, imágenes, logotipos, software) son propiedad 
              de Reserberry o de sus licenciantes y están protegidos por las leyes de propiedad intelectual. 
              Queda prohibida su reproducción sin autorización expresa.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">11. Limitación de Responsabilidad</h2>
            <p className="mb-4">Reserberry no será responsable de:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Interrupciones temporales del servicio por mantenimiento o causas técnicas.</li>
              <li>Daños derivados del uso inadecuado de la Aplicación por parte del usuario.</li>
              <li>Contenidos o servicios proporcionados por los gimnasios asociados.</li>
              <li>Pérdidas de datos causadas por el usuario.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">12. Modificaciones</h2>
            <p>
              Nos reservamos el derecho de modificar estos Términos y Condiciones en cualquier momento. 
              Los cambios serán notificados a través de la Aplicación. El uso continuado de la Aplicación 
              tras la notificación implica la aceptación de los nuevos términos.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">13. Baja y Eliminación de Cuenta</h2>
            <p className="mb-4">
              El usuario puede solicitar la eliminación de su cuenta en cualquier momento contactando 
              con soporte. La eliminación de la cuenta implica:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Pérdida de acceso a la Aplicación.</li>
              <li>Cancelación de membresías activas (sin reembolso del período en curso).</li>
              <li>Eliminación de datos personales conforme a nuestra Política de Privacidad.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">14. Legislación Aplicable</h2>
            <p>
              Estos Términos se rigen por la legislación española. Para cualquier controversia, 
              las partes se someten a los Juzgados y Tribunales de Málaga, España.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-4">15. Contacto</h2>
            <p>
              Para cualquier consulta sobre estos Términos y Condiciones, contacte con nosotros en:{' '}
              <a href="mailto:navora.citas@gmail.com" className="text-amber-400 hover:text-amber-300">
                navora.citas@gmail.com
              </a>
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} Reserberry. Todos los derechos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;













